import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { endpoints, urlApiArquivos, urlApiLaserchip, urlApiPortalClienteV2 } from 'src/environments/endpoints';
import {
  Administradora,
  AdministradoraParametro,
  PARAMETRO_IMAGEM_FUNDO_LOGIN,
  PARAMETRO_LOGO,
  TenantV2Response,
} from '../../models/administradora-parametro.model';

/**
 * Consome Administradora/ParametrosResumidos e Administradora/ParametrosCompletos
 * (host `urlApiLaserchip`). Ambos exigem o header `Authorization: Bearer
 * <partnerToken>` — o mesmo JWT estático de parceiro embutido no bundle
 * público (ver environment.model.ts), não o token de sessão do usuário
 * logado. Por isso este serviço monta seus próprios headers em vez de
 * depender do AuthInterceptor.
 *
 * CONFIRMADO lendo a resposta real (tenant conectaplan, 24/08/2026): o
 * payload é o objeto `Administradora` inteiro (CNPJ, endereço, cores, lista
 * `parametros[]` etc. — ver core/models/administradora-parametro.model.ts),
 * não só a lista de parâmetros. Para o tenant `affixbeneficios` em
 * homologação, o endpoint responde HTTP 204 (sem cadastro) — então, na
 * prática, esse tenant roda com os defaults do LayoutService.
 */
@Injectable({
  providedIn: 'root',
})
export class AdministradoraService {
  private administradora: Administradora | null = null;

  private readonly partnerHeaders = new HttpHeaders({
    Authorization: `Bearer ${environment.partnerToken}`,
  });

  constructor(private http: HttpClient) {}

  getParametrosResumidos(tenantSlug: string): Observable<Administradora | null> {
    return this.http
      .get<Administradora>(
        `${urlApiLaserchip}${endpoints.administradora.parametrosResumidos(tenantSlug)}`,
        { headers: this.partnerHeaders }
      )
      .pipe(map((response) => response ?? null));
  }

  getParametrosCompletos(tenantSlug: string): Observable<Administradora | null> {
    return this.http
      .get<Administradora>(
        `${urlApiLaserchip}${endpoints.administradora.parametrosCompletos(tenantSlug)}`,
        { headers: this.partnerHeaders }
      )
      .pipe(map((response) => response ?? null));
  }

  /**
   * Ponto único chamado pelo boot do tenant (ver
   * `loadAdministradoraAttempt$` em core/store/effects.ts) — decide entre o
   * fluxo novo e o legado a partir de `environment.useV2Boot`, sem que o
   * effect precise conhecer os dois caminhos.
   *
   * `false` (default em todo ambiente, ver environment.model.ts): fluxo
   * ORIGINAL, só Affix (`getParametrosCompletos`), sem depender de nenhum
   * backend .NET rodando.
   * `true`: fluxo novo via `getAdministradoraViaV2`.
   */
  getAdministradora(tenantSlug: string): Observable<Administradora | null> {
    return environment.useV2Boot
      ? this.getAdministradoraViaV2(tenantSlug)
      : this.getParametrosCompletos(tenantSlug);
  }

  /**
   * Boot via portal-cliente-v2-backend (host `urlApiPortalClienteV2`) —
   * branding + parametros (categoria "1") vêm de lá numa chamada só.
   * `iCodAdm` ainda vem de `getParametrosResumidos` (Affix) em paralelo,
   * porque o login atual (`PossuiSenhaExterna`) ainda exige esse campo — só
   * dá pra tirar essa segunda chamada quando o AuthController novo
   * (baseado no GVP, usando `tenantId`/`tenantProtheus` em vez de
   * `iCodAdm`) substituir o login inteiro.
   *
   * Se a chamada ao v2 falhar, cai para `resumido` sozinho — mesmo
   * comportamento do fluxo legado, sem branding rico. Só é chamado quando
   * `environment.useV2Boot` está `true` (ver `getAdministradora` acima).
   */
  getAdministradoraViaV2(tenantSlug: string): Observable<Administradora | null> {
    return forkJoin({
      resumido: this.getParametrosResumidos(tenantSlug),
      v2: this.http
        .get<TenantV2Response>(`${urlApiPortalClienteV2}tenants/${tenantSlug}`)
        .pipe(catchError(() => of(null))),
    }).pipe(
      map(({ resumido, v2 }) => {
        if (!resumido) return null;
        if (!v2) return resumido;

        const parametros: AdministradoraParametro[] = v2.parametros.map((p) => ({
          sParametro: p.parametro,
          sValor: p.valor,
          sTipo: p.tipo,
          sDscParametro: p.dscParametro,
          sTamanhoImagem: p.tamanhoImagem,
        }));

        return { ...resumido, parametros, tenantId: v2.tenantProtheus };
      })
    );
  }

  /**
   * Guarda a Administradora carregada (chamado pelo effect de boot, ver
   * core/store/effects.ts) para consulta síncrona por
   * `getParametroValor`/`getPermissao`/`getLogoUrl` em qualquer lugar do
   * app.
   */
  setAdministradora(administradora: Administradora | null): void {
    this.administradora = administradora;
  }

  /**
   * Réplica da lógica de lookup confirmada no bundle real (nome ofuscado,
   * lógica reproduzida): busca o valor de um parâmetro pelo nome.
   */
  getParametroValor(nome: string): string | null | undefined {
    return this.administradora?.parametros.find((p) => p.sParametro === nome)?.sValor;
  }

  /**
   * `sValor` de `sUrlLogo1`/`sUrlFundoLogin` só é a URL final da imagem
   * quando `environment.useV2Boot: true` (decisão de 27/08/2026: nesse
   * fluxo as imagens migraram do S3 assinado da Affix pro Azure Blob, URL
   * pública fixa, sem autenticação nem expiração — não precisa de nenhum
   * passo de resolução).
   *
   * BUG encontrado em 27/08/2026: `useV2Boot` passou a ter default `false`
   * em todo ambiente (fluxo legado, `getParametrosCompletos`), e nesse
   * fluxo o app real AINDA resolve logo/fundo via uma chamada separada —
   * CONFIRMADO ao vivo (tenant conectaplan) via
   * `performance.getEntriesByType('resource')`: `Arquivo/URL/{idTipo}/
   * {arquivoId}` (host `urlApiArquivos`, idTipo `5` para logo/fundo, já
   * documentado em `endpoints.arquivos.url`). Sem esse passo, `sValor`
   * (só o id do arquivo no fluxo legado) era usado direto como `src`,
   * gerando uma imagem quebrada.
   *
   * TODO: o formato exato da resposta de `Arquivo/URL` não foi confirmado
   * (string pura vs. `{ url }`) — o README só confirma que "retorna uma
   * URL assinada da AWS S3". Ajustar `resolveArquivoUrl` se a resposta
   * real vier envelopada.
   */
  private resolveArquivoUrl(valor: string | null | undefined, idTipo = 5): Observable<string | undefined> {
    if (!valor) return of(undefined);

    // Fluxo v2 (Blob) ou um `sValor` que já veio como URL pronta — nos dois
    // casos não há nada a resolver.
    if (environment.useV2Boot || valor.startsWith('http')) {
      return of(valor);
    }

    return this.http
      .get<string>(`${urlApiArquivos}${endpoints.arquivos.url(idTipo, valor)}`)
      .pipe(catchError(() => of(undefined)));
  }

  getLogoUrl(): Observable<string | undefined> {
    return this.resolveArquivoUrl(this.getParametroValor(PARAMETRO_LOGO));
  }

  getBackgroundLoginUrl(): Observable<string | undefined> {
    return this.resolveArquivoUrl(this.getParametroValor(PARAMETRO_IMAGEM_FUNDO_LOGIN));
  }

  /**
   * Réplica da checagem de bitmask confirmada no bundle real:
   * `(iPermissaoAgrupada & flag) > 0`.
   * TODO: os valores reais das flags (quais bits representam o quê) não
   * foram confirmados.
   */
  getPermissao(flag: number): boolean {
    return ((this.administradora?.iPermissaoAgrupada ?? 0) & flag) > 0;
  }

  /**
   * Código numérico da Administradora do tenant (ex.: `4` para conectaplan)
   * — CONFIRMADO em 25/08/2026 como um dos campos exigidos pelo passo 1 do
   * login (`PossuiSenhaExterna`, ver core/models/auth.model.ts).
   */
  getICodAdm(): number | undefined {
    return this.administradora?.iCodAdm;
  }

  /**
   * Campos fixos do cadastro da Administradora (não são `sParametro`, ver
   * comentário em administradora-parametro.model.ts) — usados pelo rodapé
   * real: `ANS - n° {codAns} {tenantNome}. Todos os direitos reservados
   * © {ano}.` (confirmado ao vivo, tenant conectaplan, 27/08/2026).
   */
  getCodAns(): string | undefined {
    return this.administradora?.sCodANS;
  }

  getNomeTenant(): string | undefined {
    return this.administradora?.sDscAdm;
  }
}
